import { useState, useEffect, useRef, useMemo } from "react";
import SearchBar from "../../Search/SearchBar";
import styles from "./Style.module.css";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";
import DotIcon from "../../Icons/DotIcon";

export const SPECIAL_REQUEST_SECTIONS = [
	{
		id: "nationality",
		label: "NATIONALITY",
		options: ["China", "Thai", "Malaysian", "Singapore", "Indonesian"],
	},
	{
		id: "gender",
		label: "GENDER",
		options: ["Female", "Male"],
	},
];

function normalizeDefaultSelected(raw) {
	if (Array.isArray(raw)) {
		return raw.map(String).map((s) => s.trim()).filter(Boolean);
	}
	if (typeof raw === "string" && raw.length > 0) {
		return raw.split(",").map((s) => s.trim()).filter(Boolean);
	}
	return [];
}

function selectionRecordFromArray(arr, sections) {
	const record = {};
	sections.forEach((s) => {
		record[s.id] = null;
	});
	for (const item of arr) {
		const sec = sections.find((s) => s.options.includes(item));
		if (sec) {
			record[sec.id] = item;
		}
	}
	return record;
}

function recordToArray(record, sections) {
	return sections.map((s) => record[s.id]).filter(Boolean);
}

const SpecialRequestDropdown = ({
	sections = SPECIAL_REQUEST_SECTIONS,
	defaultSelected = [],
	labelText = "",
	placeholder = "Select",
	dropdownClass = "",
	placeholderClass = styles.placeholder,
	onChange = () => {},
}) => {
	const [selection, setSelection] = useState(() =>
		selectionRecordFromArray(normalizeDefaultSelected(defaultSelected), sections)
	);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);

	const defaultSerialized = useMemo(
		() => JSON.stringify(normalizeDefaultSelected(defaultSelected)),
		[defaultSelected]
	);

	useEffect(() => {
		setSelection(
			selectionRecordFromArray(
				JSON.parse(defaultSerialized),
				sections
			)
		);
	}, [defaultSerialized, sections]);

	const toggleDropdown = () => {
		setDropdownOpen((prev) => !prev);
		if (!isDropdownOpen) {
			setSearchQuery("");
		}
	};

	const flatOptions = useMemo(() => {
		const q = searchQuery.toLowerCase().trim();
		const out = [];
		for (const sec of sections) {
			const opts = sec.options.filter(
				(o) => !q || o.toLowerCase().includes(q)
			);
			for (const value of opts) {
				out.push({ sectionId: sec.id, value });
			}
		}
		return out;
	}, [sections, searchQuery]);

	const filteredSections = useMemo(() => {
		const q = searchQuery.toLowerCase().trim();
		return sections
			.map((sec) => ({
				...sec,
				options: sec.options.filter(
					(o) => !q || o.toLowerCase().includes(q)
				),
			}))
			.filter((sec) => sec.options.length > 0);
	}, [sections, searchQuery]);

	const handleSectionOptionClick = (sectionId, value) => {
		setSelection((prev) => {
			const next = { ...prev };
			if (next[sectionId] === value) {
				next[sectionId] = null;
			} else {
				next[sectionId] = value;
			}
			onChange(recordToArray(next, sections));
			return next;
		});
	};

	const handleNone = () => {
		const cleared = {};
		sections.forEach((s) => {
			cleared[s.id] = null;
		});
		setSelection(cleared);
		onChange([]);
		setDropdownOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const displayArray = recordToArray(selection, sections);
	const displayLabel =
		displayArray.length > 0 ? displayArray.join(", ") : placeholder;

	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	const totalNavItems = 1 + flatOptions.length;

	const handleSelectByIndex = (index) => {
		if (index === 0) {
			handleNone();
		} else {
			const row = flatOptions[index - 1];
			if (row) {
				handleSectionOptionClick(row.sectionId, row.value);
			}
		}
	};

	const {
		selectedIndex,
		setSelectedIndex,
		handleKeyDown: handleKeyboardNav,
		setIsKeyboardNavActive,
		isKeyboardNavActive,
	} = useKeyboardNavigation(totalNavItems, handleSelectByIndex, {
		listRef: dropdownMenuRef,
	});

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (i) => i);
	};

	let nextNavIndex = 0;
	const noneNavIndex = nextNavIndex++;

	return (
		<div
			ref={dropdownRef}
			className={`${dropdownClass} commonDropdownNoImage`}
			tabIndex={0}>
			<div className={`dropdownToggle cursor-pointer ${displayArray.length === 0 ? placeholderClass : ""}`}
				onClick={toggleDropdown}
			>
				{labelText} {displayLabel}
			</div>
			{isDropdownOpen && (
				<div className={styles.dropdownContainer} ref={dropdownMenuRef}>
					<div className={styles.header}>
						<SearchBar
							ref={searchInputRef}
							placeholder="Search..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={handleKeyDown}
							className="border-0"
						/>
					</div>
					<div className={styles.dropdownMenu}>
						<div
							className={`${styles.dropdownItem} ${styles.item} ${
								selectedIndex === noneNavIndex
									? styles.keyboardSelected
									: ""
							}`}
							data-key-nav-index={noneNavIndex}
							onClick={handleNone}
							onMouseEnter={() => {
								if (!isKeyboardNavActive) setSelectedIndex(noneNavIndex);
							}}
							onMouseMove={() => {
								if (isKeyboardNavActive) {
									setIsKeyboardNavActive(false);
									setSelectedIndex(noneNavIndex);
								}
							}}>
							None
						</div>
					{filteredSections.length === 0 ? (
						<div className={`${styles.dropdownItem} no-item-found-placeholder`}>
							No options found
						</div>
					) : (
						filteredSections.map((sec) => (
							<div key={sec.id}>
								<div
									className={styles.categoryHeading}>
									{sec.label}
								</div>
								<div >
									{sec.options.map((opt) => {
										const navIndex = nextNavIndex++;
										const isChosen = selection[sec.id] === opt;
										return (
											<div
												key={`${sec.id}-${opt}`}
												className={`${styles.item} ${styles.categoryItem} ${
													selectedIndex === navIndex
														? styles.keyboardSelected
														: ""
												} ${
													isChosen
														? styles.selectedItem
														: ""
												}`}
												data-key-nav-index={navIndex}
												onClick={() =>
													handleSectionOptionClick(
														sec.id,
														opt
													)
												}
												onMouseEnter={() => {
													if (!isKeyboardNavActive)
														setSelectedIndex(
															navIndex
														);
												}}
												onMouseMove={() => {
													if (isKeyboardNavActive) {
														setIsKeyboardNavActive(
															false
														);
														setSelectedIndex(
															navIndex
														);
													}
												}}>
												{opt}
												{isChosen && <DotIcon color="var(--primary-900)" />}
											</div>
										);
									})}
								</div>
							</div>
						))
					)}
					</div>
				</div>
			)}
		</div>
	);
};

export default SpecialRequestDropdown;
